import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../Auth/AuthContext";

const API = "https://api.sigmasystems.com.br/api/ag-barber";

/* ---------- helpers ---------- */

const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const inteiro = (v) => Number(v || 0).toLocaleString("pt-BR");
const NOMES_DIA = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
const DIA_CURTO = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

const PERIODOS = [
  { v: "hoje", l: "Hoje" },
  { v: "semana", l: "Semana" },
  { v: "mes", l: "Mês" },
  { v: "mes_passado", l: "Mês passado" },
];

// variação percentual de "atual" sobre "base"; null quando não dá para comparar
const variacao = (atual, base) => (base > 0 ? ((atual - base) / base) * 100 : null);
const pct = (v) => `${Math.abs(v).toFixed(0)}%`;
const porcento = (parte, todo) => (todo > 0 ? (parte / todo) * 100 : 0);

const maiorPor = (lista, campo) => {
  const ordenada = [...lista].sort((a, b) => b[campo] - a[campo]);
  return ordenada.length && ordenada[0][campo] > 0 ? ordenada[0] : null;
};

// lista de dias "AAAA-MM-DD" entre `de` (inclusive) e `ate` (exclusivo)
function diasDoPeriodo(de, ate) {
  const dias = [];
  for (let d = dayjs(de); d.isBefore(dayjs(ate)); d = d.add(1, "day")) dias.push(d.format("YYYY-MM-DD"));
  return dias;
}

const IconeDashboard = () => (
  <svg className="sideMenu-btn-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="3" width="7" height="9" /><rect x="14" y="3" width="7" height="5" />
    <rect x="14" y="12" width="7" height="9" /><rect x="3" y="16" width="7" height="5" />
  </svg>
);

/* ---------- blocos pequenos ---------- */

function Variacao({ atual, base, rotulo }) {
  const v = variacao(atual, base);
  if (v === null) return <span className="dash-var dash-var--neutra">{rotulo} sem base de comparação</span>;
  const igual = Math.abs(v) < 0.5;
  const classe = igual ? "dash-var--neutra" : v > 0 ? "dash-var--sobe" : "dash-var--desce";
  return (
    <span className={`dash-var ${classe}`}>
      {igual ? "= " : v > 0 ? "▲ " : "▼ "}{pct(v)} {rotulo}
    </span>
  );
}

function Cartao({ titulo, valor, linhas = [], destaque }) {
  return (
    <div className={`dash-card${destaque ? " dash-card--destaque" : ""}`}>
      <div className="dash-card__titulo">{titulo}</div>
      <div className="dash-card__valor">{valor}</div>
      {linhas.filter(Boolean).map((l, i) => <div key={i} className="dash-card__linha">{l}</div>)}
    </div>
  );
}

function Bloco({ titulo, direita, children, className = "" }) {
  return (
    <section className={`dash-bloco ${className}`}>
      <div className="dash-bloco__topo">
        <h2>{titulo}</h2>
        {direita}
      </div>
      {children}
    </section>
  );
}

function Alternar({ opcoes, valor, onChange }) {
  return (
    <div className="dash-toggle" role="group">
      {opcoes.map((o) => (
        <button key={o.v} type="button" className={valor === o.v ? "ativo" : ""} onClick={() => onChange(o.v)}>
          {o.l}
        </button>
      ))}
    </div>
  );
}

// ranking horizontal (top 5)
function ListaTop({ itens, modo, vazio, mostrarSemComissao }) {
  if (!itens.length) return <div className="dash-vazio">{vazio}</div>;
  const campo = modo === "qtd" ? "qtd" : "valor";
  const maximo = Math.max(...itens.map((i) => i[campo]), 1);
  return (
    <ol className="dash-top">
      {itens.map((i, idx) => (
        <li key={`${i.nome}-${idx}`}>
          <span className="dash-top__pos">{idx + 1}</span>
          <div className="dash-top__corpo">
            <div className="dash-top__linha">
              <span className="dash-top__nome" title={i.nome}>
                {i.nome}
                {mostrarSemComissao && i.comissao === false && <em> · sem comissão</em>}
              </span>
              <span className="dash-top__num">
                <strong>{inteiro(i.qtd)}x</strong> · {brl(i.valor)}
              </span>
            </div>
            <div className="dash-barra"><div style={{ width: `${(i[campo] / maximo) * 100}%` }} /></div>
          </div>
        </li>
      ))}
    </ol>
  );
}

// gráfico de barras por dia do período
function GraficoDias({ dados, de, ate, campo = "total" }) {
  const dias = useMemo(() => diasDoPeriodo(de, ate), [de, ate]);
  const mapa = useMemo(() => new Map(dados.map((d) => [d.dia, d])), [dados]);
  const maximo = Math.max(...dias.map((d) => mapa.get(d)?.[campo] ?? 0), 1);
  const poucos = dias.length <= 8;
  return (
    <div className={`dash-grafico${poucos ? " dash-grafico--poucos" : ""}`}>
      {dias.map((d) => {
        const x = mapa.get(d);
        const valor = x?.[campo] ?? 0;
        const dt = dayjs(d);
        return (
          <div key={d} className="dash-grafico__col"
            title={`${dt.format("DD/MM")} (${DIA_CURTO[dt.day()]}): ${brl(valor)}${x ? ` · ${x.vendas} lançamento(s)` : ""}`}>
            <div className="dash-grafico__valor">{valor > 0 && poucos ? brl(valor) : ""}</div>
            <div className="dash-grafico__area">
              <div className={`dash-grafico__barra${valor === 0 ? " vazia" : ""}`} style={{ height: `${Math.max((valor / maximo) * 100, valor ? 3 : 0)}%` }} />
            </div>
            <div className="dash-grafico__rotulo">{poucos ? DIA_CURTO[dt.day()] : dt.format("D")}</div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------- tela ---------- */

function Dashboard() {
  const { nome, ehAdmin, logout } = useAuth();
  const navigate = useNavigate();

  const [periodo, setPeriodo] = useState("mes");
  const [profFiltro, setProfFiltro] = useState("");   // só ADMIN: "" = barbearia toda
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState("");
  const [carregando, setCarregando] = useState(true);
  const [modoServ, setModoServ] = useState("valor");
  const [modoProd, setModoProd] = useState("valor");
  const [atualizadoEm, setAtualizadoEm] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro("");
    try {
      const { data } = await axios.get(`${API}/dashboard`, {
        params: { periodo, ...(ehAdmin && profFiltro ? { user_id: profFiltro } : {}) },
      });
      setDados(data);
      setAtualizadoEm(dayjs());
    } catch (e) {
      setErro(e.response?.data?.message || "Não foi possível carregar o dashboard.");
    } finally {
      setCarregando(false);
    }
  }, [periodo, profFiltro, ehAdmin]);

  useEffect(() => { carregar(); }, [carregar]);

  const geral = ehAdmin && dados?.visao === "geral";
  const rotuloPeriodo = periodo === "hoje" ? "hoje" : periodo === "semana" ? "esta semana"
    : periodo === "mes" ? "este mês" : "no mês passado";

  const nomeFiltrado = dados?.profissionais_lista?.find((p) => String(p.id) === String(profFiltro))?.nome;

  /* ----- destaques e avisos (calculados no front, a partir dos dados) ----- */
  const destaques = useMemo(() => {
    if (!geral || !dados?.profissionais?.length) return [];
    const P = dados.profissionais;
    const itens = [
      ["Quem mais rende", maiorPor(P, "total"), (p) => brl(p.total)],
      ["Mais serviços feitos", maiorPor(P, "qtd_servicos"), (p) => `${inteiro(p.qtd_servicos)} serviços`],
      ["Mais produtos vendidos", maiorPor(P, "qtd_produtos"), (p) => `${inteiro(p.qtd_produtos)} unidades`],
      ["Maior ticket médio", maiorPor(P, "ticket_medio"), (p) => brl(p.ticket_medio)],
      ["Mais clientes atendidos", maiorPor(P, "clientes"), (p) => `${inteiro(p.clientes)} clientes`],
    ];
    return itens.filter(([, p]) => p).map(([titulo, p, fmt]) => ({ titulo, nome: p.nome, valor: fmt(p) }));
  }, [geral, dados]);

  const avisos = useMemo(() => {
    if (!dados) return [];
    const lista = [];
    const m = dados.cartoes.mes;
    const ant = dados.mes_passado_ate_hoje;
    const v = variacao(m.total, ant.total);

    if (geral) {
      if (v !== null && v <= -5) lista.push({ tipo: "atencao", texto: `O faturamento do mês está ${pct(v)} abaixo do mesmo período do mês passado (${brl(m.total)} contra ${brl(ant.total)}).` });
      else if (v !== null && v >= 5) lista.push({ tipo: "ok", texto: `O faturamento do mês está ${pct(v)} acima do mesmo período do mês passado.` });

      if (dados.sem_lancamento?.length)
        lista.push({ tipo: "atencao", texto: `Sem nenhum lançamento ${rotuloPeriodo}: ${dados.sem_lancamento.map((p) => p.nome).join(", ")}.` });

      const r = dados.periodo_resumo;
      if (r.total > 0) {
        const partProd = porcento(r.produtos, r.total);
        if (partProd < 10) lista.push({ tipo: "dica", texto: `Produtos representam só ${partProd.toFixed(0)}% do faturamento ${rotuloPeriodo}. Oferecer um produto no atendimento é a forma mais rápida de aumentar o ticket médio.` });
        const partSem = porcento(r.produtos_sem_comissao, r.total);
        if (partSem >= 25) lista.push({ tipo: "dica", texto: `${partSem.toFixed(0)}% do faturamento ${rotuloPeriodo} vem de produtos sem comissão.` });
      }

      const dw = dados.dias_semana.filter((d) => d.vendas > 0);
      if (dw.length >= 3) {
        const melhor = [...dw].sort((a, b) => b.media_por_dia - a.media_por_dia)[0];
        const pior = [...dw].sort((a, b) => a.media_por_dia - b.media_por_dia)[0];
        lista.push({ tipo: "info", texto: `Nos últimos 90 dias, ${NOMES_DIA[melhor.dow].toLowerCase()} é o dia que mais rende (média de ${brl(melhor.media_por_dia)}) e ${NOMES_DIA[pior.dow].toLowerCase()} o mais fraco (${brl(pior.media_por_dia)}). Uma promoção ou aviso aos clientes nos dias fracos pode ajudar.` });
      }
    } else {
      if (v !== null && v >= 5) lista.push({ tipo: "ok", texto: `Você está ${pct(v)} acima do mesmo período do mês passado. Continue assim!` });
      if (v !== null && v <= -5) lista.push({ tipo: "atencao", texto: `Você está ${pct(v)} abaixo do mesmo período do mês passado.` });

      const falta = dados.mes_passado.comissao - m.comissao;
      if (dados.mes_passado.comissao > 0 && falta > 0)
        lista.push({ tipo: "info", texto: `Faltam ${brl(falta)} de comissão para igualar o que você fez no mês passado inteiro (${brl(dados.mes_passado.comissao)}).` });
      else if (dados.mes_passado.comissao > 0)
        lista.push({ tipo: "ok", texto: "Sua comissão deste mês já passou a do mês passado inteiro." });

      const r = dados.periodo_resumo;
      if (r.total > 0 && porcento(r.produtos, r.total) < 10)
        lista.push({ tipo: "dica", texto: `Produtos são só ${porcento(r.produtos, r.total).toFixed(0)}% do que você vendeu ${rotuloPeriodo}. Cada produto com comissão vendido aumenta a sua comissão.` });

      const melhorDia = [...(dados.por_dia ?? [])].sort((a, b) => b.total - a.total)[0];
      if (melhorDia && melhorDia.total > 0)
        lista.push({ tipo: "info", texto: `Seu melhor dia ${rotuloPeriodo} foi ${dayjs(melhorDia.dia).format("DD/MM")} (${DIA_CURTO[dayjs(melhorDia.dia).day()]}), com ${brl(melhorDia.total)}.` });
    }
    return lista;
  }, [dados, geral, rotuloPeriodo]);

  /* ----- menu lateral (igual ao das outras telas) ----- */
  const menu = (
    <div className="sideMenu">
      <div className="sideMenu-content">
        <div className="sideMenu-orientation">
          <div className="sideMenu-top">
            <img src="/Images/bot-3d.png" className="sideMenu-bot3d" alt="" />
            <h3 className="sideMenu-name">{nome}</h3>
          </div>
          <div className="sideMenu-btns">
            <Link className="sideMenu-option-active" to="/Dashboard">
              <IconeDashboard />
              <span className="sideMenu-title">Dashboard</span>
            </Link>
            <Link className="sideMenu-option" to="/Agendamentos">
              <img src="/Images/calendar.svg" className="sideMenu-btn-icon" alt="" />
              <span className="sideMenu-title">Agendamentos</span>
            </Link>
            {ehAdmin && (
              <>
                <Link className="sideMenu-option" to="/Configuracoes">
                  <img src="/Images/options.svg" className="sideMenu-btn-icon" alt="" />
                  <span className="sideMenu-title">Configuração</span>
                </Link>
                <Link className="sideMenu-option" to="/Lancamentos">
                  <img src="/Images/bag.svg" className="sideMenu-btn-icon" alt="" />
                  <span className="sideMenu-title">Lançamentos</span>
                </Link>
              </>
            )}
          </div>
        </div>
        <div className="sideMenu-logout">
          <div className="sideMenu-logout-btn" onClick={() => { logout(); navigate("/"); }}>
            <img src="/Images/logout.svg" className="sideMenu-logout-icon" alt="" />
            <span className="sideMenu-title">Sair</span>
          </div>
          <img className="sideMenu-logo" src="/Images/sigmaBranco.png" alt="Sigma Systems" />
        </div>
      </div>
    </div>
  );

  /* ----- conteúdo ----- */
  const c = dados?.cartoes;
  const r = dados?.periodo_resumo;

  const subtitulo = geral
    ? "Visão geral da barbearia"
    : ehAdmin && nomeFiltrado ? `Números de ${nomeFiltrado}` : "Seus números";

  return (
    <div className="container-ag">
      <div className="content-ag">
        {menu}

        <div className="content">
          <div className="content-top">
            <div className="content-title-wrap">
              <h1 className="content-title">Dashboard</h1>
              <span className="comissao-mes">{subtitulo}</span>
            </div>
            <div className="dash-controles">
              {ehAdmin && dados?.profissionais_lista && (
                <select className="lanc-input dash-select" value={profFiltro} onChange={(e) => setProfFiltro(e.target.value)}
                  aria-label="Filtrar por profissional">
                  <option value="">Barbearia toda</option>
                  {dados.profissionais_lista.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
                </select>
              )}
              <Alternar opcoes={PERIODOS} valor={periodo} onChange={setPeriodo} />
              <button type="button" className="dash-atualizar" onClick={carregar} disabled={carregando} title="Atualizar">
                {carregando ? "..." : "↻"}
              </button>
            </div>
          </div>

          <div className="content-block dash">
            {erro && <div className="dash-erro">{erro}</div>}
            {!dados && carregando && <div className="dash-vazio">Carregando...</div>}

            {dados && (
              <>
                {/* ===== cartões: hoje, semana, mês ===== */}
                <div className="dash-grade dash-grade--3">
                  {[["hoje", "Hoje"], ["semana", "Esta semana"], ["mes", "Este mês"]].map(([k, titulo]) => (
                    <Cartao key={k} titulo={`Lançamentos · ${titulo}`} valor={brl(c[k].total)} destaque={k === "mes"}
                      linhas={[
                        `${inteiro(c[k].vendas)} lançamento${c[k].vendas === 1 ? "" : "s"} · ticket médio ${brl(c[k].ticket_medio)}`,
                        `Comissão ${geral ? "paga" : "sua"}: ${brl(c[k].comissao)}`,
                        geral && `Fica para a barbearia: ${brl(c[k].liquido)}`,
                      ]} />
                  ))}
                </div>

                {/* ===== comparação + agenda ===== */}
                <div className="dash-grade dash-grade--3">
                  <Cartao titulo="Mês atual x mês passado (mesmo período)" valor={brl(c.mes.total)}
                    linhas={[
                      <Variacao key="v" atual={c.mes.total} base={dados.mes_passado_ate_hoje.total} rotulo="vs. mesmo período do mês passado" />,
                      `No mês passado até esta data: ${brl(dados.mes_passado_ate_hoje.total)}`,
                      `Mês passado inteiro: ${brl(dados.mes_passado.total)}`,
                    ]} />
                  <Cartao titulo={geral ? "Comissões do mês" : "Sua comissão do mês"} valor={brl(c.mes.comissao)}
                    linhas={[
                      `Serviços: ${brl(c.mes.comissao_servicos)} · Produtos: ${brl(c.mes.comissao_produtos)}`,
                      <Variacao key="v2" atual={c.mes.comissao} base={dados.mes_passado_ate_hoje.comissao} rotulo="vs. mesmo período do mês passado" />,
                    ]} />
                  <Cartao titulo="Agendamentos" valor={inteiro(dados.agendamentos.hoje)}
                    linhas={[
                      `agendado${dados.agendamentos.hoje === 1 ? "" : "s"} para hoje`,
                      `${inteiro(dados.agendamentos.proximos_7_dias)} nos próximos 7 dias`,
                    ]} />
                </div>

                {/* ===== avisos ===== */}
                {avisos.length > 0 && (
                  <Bloco titulo={geral ? "O que merece atenção" : "Para você"}>
                    <ul className="dash-avisos">
                      {avisos.map((a, i) => <li key={i} className={`dash-aviso dash-aviso--${a.tipo}`}>{a.texto}</li>)}
                    </ul>
                  </Bloco>
                )}

                {/* ===== composição do período ===== */}
                <Bloco titulo={`Composição ${rotuloPeriodo}`}
                  direita={<span className="dash-bloco__total">Total {brl(r.total)}</span>}>
                  <div className="dash-grade dash-grade--3 dash-grade--interna">
                    <div className="dash-mini">
                      <span className="dash-mini__rotulo"><i className="dash-dot dash-dot--serv" /> Serviços</span>
                      <strong>{brl(r.servicos)}</strong>
                      <small>{porcento(r.servicos, r.total).toFixed(0)}% do total · comissão {brl(r.comissao_servicos)}</small>
                    </div>
                    <div className="dash-mini">
                      <span className="dash-mini__rotulo"><i className="dash-dot dash-dot--prod" /> Produtos com comissão</span>
                      <strong>{brl(r.produtos_com_comissao)}</strong>
                      <small>{porcento(r.produtos_com_comissao, r.total).toFixed(0)}% do total · comissão {brl(r.comissao_produtos)}</small>
                    </div>
                    <div className="dash-mini">
                      <span className="dash-mini__rotulo"><i className="dash-dot dash-dot--sem" /> Produtos sem comissão</span>
                      <strong>{brl(r.produtos_sem_comissao)}</strong>
                      <small>{porcento(r.produtos_sem_comissao, r.total).toFixed(0)}% do total</small>
                    </div>
                  </div>
                  <div className="dash-empilhada" aria-hidden="true">
                    <div className="dash-empilhada__serv" style={{ width: `${porcento(r.servicos, r.total)}%` }} />
                    <div className="dash-empilhada__prod" style={{ width: `${porcento(r.produtos_com_comissao, r.total)}%` }} />
                    <div className="dash-empilhada__sem" style={{ width: `${porcento(r.produtos_sem_comissao, r.total)}%` }} />
                  </div>
                  <div className="dash-resumo-linha">
                    <span>{inteiro(r.vendas)} lançamento{r.vendas === 1 ? "" : "s"}</span>
                    <span>{inteiro(r.clientes)} cliente{r.clientes === 1 ? "" : "s"}</span>
                    <span>Ticket médio {brl(r.ticket_medio)}</span>
                    <span>Comissão total {brl(r.comissao)}</span>
                    {geral && <span>Líquido da barbearia {brl(r.liquido)}</span>}
                  </div>
                </Bloco>

                {/* ===== evolução diária ===== */}
                <Bloco titulo={`Evolução ${periodo === "hoje" ? "de hoje" : "dia a dia"}`}
                  direita={<span className="dash-bloco__total">{dayjs(dados.de).format("DD/MM")} a {dayjs(dados.ate_exclusivo).subtract(1, "day").format("DD/MM")}</span>}>
                  {dados.por_dia.length
                    ? <GraficoDias dados={dados.por_dia} de={dados.de} ate={dados.ate_exclusivo} />
                    : <div className="dash-vazio">Nenhum lançamento {rotuloPeriodo}.</div>}
                </Bloco>

                {/* ===== top 5 ===== */}
                <div className="dash-grade dash-grade--2">
                  <Bloco titulo="Top 5 serviços"
                    direita={<Alternar opcoes={[{ v: "valor", l: "Valor" }, { v: "qtd", l: "Qtd." }]} valor={modoServ} onChange={setModoServ} />}>
                    <ListaTop itens={modoServ === "qtd" ? dados.top_servicos.por_quantidade : dados.top_servicos.por_valor}
                      modo={modoServ} vazio={`Nenhum serviço lançado ${rotuloPeriodo}.`} />
                  </Bloco>
                  <Bloco titulo="Top 5 produtos"
                    direita={<Alternar opcoes={[{ v: "valor", l: "Valor" }, { v: "qtd", l: "Qtd." }]} valor={modoProd} onChange={setModoProd} />}>
                    <ListaTop itens={modoProd === "qtd" ? dados.top_produtos.por_quantidade : dados.top_produtos.por_valor}
                      modo={modoProd} vazio={`Nenhum produto vendido ${rotuloPeriodo}.`} mostrarSemComissao />
                  </Bloco>
                </div>

                {/* ===== só ADMIN, visão geral ===== */}
                {geral && (
                  <>
                    {destaques.length > 0 && (
                      <Bloco titulo={`Destaques da equipe · ${rotuloPeriodo}`}>
                        <div className="dash-destaques">
                          {destaques.map((d) => (
                            <div key={d.titulo} className="dash-destaque">
                              <small>{d.titulo}</small>
                              <strong>{d.nome}</strong>
                              <span>{d.valor}</span>
                            </div>
                          ))}
                        </div>
                      </Bloco>
                    )}

                    <Bloco titulo={`Desempenho por profissional · ${rotuloPeriodo}`}>
                      {dados.profissionais.length ? (
                        <div className="dash-tabela-wrap">
                          <table className="dash-tabela">
                            <thead>
                              <tr>
                                <th>Profissional</th><th>Faturado</th><th>Serviços</th><th>Produtos</th>
                                <th>Clientes</th><th>Ticket médio</th><th>Comissão</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dados.profissionais.map((p) => (
                                <tr key={p.id}>
                                  <td data-rotulo="Profissional"><strong>{p.nome}</strong></td>
                                  <td data-rotulo="Faturado">{brl(p.total)}</td>
                                  <td data-rotulo="Serviços">{inteiro(p.qtd_servicos)} · {brl(p.servicos)}</td>
                                  <td data-rotulo="Produtos">{inteiro(p.qtd_produtos)} · {brl(p.produtos)}</td>
                                  <td data-rotulo="Clientes">{inteiro(p.clientes)}</td>
                                  <td data-rotulo="Ticket médio">{brl(p.ticket_medio)}</td>
                                  <td data-rotulo="Comissão">{brl(p.comissao)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : <div className="dash-vazio">Nenhum lançamento {rotuloPeriodo}.</div>}
                    </Bloco>

                    <Bloco titulo="Comissões de cada profissional">
                      <div className="dash-tabela-wrap">
                        <table className="dash-tabela">
                          <thead><tr><th>Profissional</th><th>Hoje</th><th>Esta semana</th><th>Este mês</th></tr></thead>
                          <tbody>
                            {dados.comissoes.map((p) => (
                              <tr key={p.id}>
                                <td data-rotulo="Profissional"><strong>{p.nome}</strong></td>
                                <td data-rotulo="Hoje">{brl(p.hoje)}</td>
                                <td data-rotulo="Esta semana">{brl(p.semana)}</td>
                                <td data-rotulo="Este mês">{brl(p.mes)}</td>
                              </tr>
                            ))}
                            <tr className="dash-tabela__total">
                              <td data-rotulo="Profissional">Total</td>
                              <td data-rotulo="Hoje">{brl(dados.comissoes.reduce((s, x) => s + x.hoje, 0))}</td>
                              <td data-rotulo="Esta semana">{brl(dados.comissoes.reduce((s, x) => s + x.semana, 0))}</td>
                              <td data-rotulo="Este mês">{brl(dados.comissoes.reduce((s, x) => s + x.mes, 0))}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </Bloco>

                    <Bloco titulo="Quais dias da semana mais rendem" direita={<span className="dash-bloco__total">média por dia · últimos 90 dias</span>}>
                      <GraficoSemana dias={dados.dias_semana} />
                    </Bloco>
                  </>
                )}

                <div className="dash-rodape">
                  {atualizadoEm && `Atualizado às ${atualizadoEm.format("HH:mm")} · `}
                  semanas contadas de segunda a domingo
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// barras por dia da semana (média de faturamento por dia trabalhado)
function GraficoSemana({ dias }) {
  const maximo = Math.max(...dias.map((d) => d.media_por_dia), 1);
  const melhor = Math.max(...dias.map((d) => d.media_por_dia));
  return (
    <div className="dash-grafico dash-grafico--poucos">
      {dias.map((d) => (
        <div key={d.dow} className="dash-grafico__col" title={`${NOMES_DIA[d.dow]}: média de ${brl(d.media_por_dia)} por dia · ${d.vendas} lançamento(s)`}>
          <div className="dash-grafico__valor">{d.media_por_dia > 0 ? brl(d.media_por_dia) : ""}</div>
          <div className="dash-grafico__area">
            <div className={`dash-grafico__barra${d.media_por_dia === 0 ? " vazia" : ""}${d.media_por_dia === melhor && melhor > 0 ? " destaque" : ""}`}
              style={{ height: `${Math.max((d.media_por_dia / maximo) * 100, d.media_por_dia ? 3 : 0)}%` }} />
          </div>
          <div className="dash-grafico__rotulo">{DIA_CURTO[d.dow]}</div>
        </div>
      ))}
    </div>
  );
}

export default Dashboard;
